import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ContainedMediaOverlay } from '../liveView/ContainedMediaOverlay';
import type { OverviewAreaGeometry } from '../../pages/OverviewAreaCameraPanel';
import {
  selectedViewMjpegRetryDelayMs,
  type SpatialOperationsLiveCameraState,
} from '../../hooks/useSpatialOperationsLiveCamera';
import {
  defaultMjpegStreamRelay,
  type MjpegRelayArgs,
  type MjpegRelayResult,
} from '../../services/multipartMjpegRelay';

export const SPATIAL_LIVE_CAMERA_LABEL = 'LIVE CAMERA';
export const SPATIAL_LIVE_CONNECTING_LABEL = 'Connecting live view...';
export const SPATIAL_LIVE_FALLBACK_LABEL = 'Live video is unavailable. Showing the latest camera image.';
export const SPATIAL_CAMERA_OFFLINE_LABEL = 'Camera offline';
/**
 * Same ceilings as the MJPEG proxy hold-last-frame gaps
 * (MJPEG_LIVE_HOLD_MAX_GAP_SEC / MJPEG_ANNOTATED_HOLD_MAX_GAP_SEC).
 * A repeated JPEG is not a new frame.
 */
export const SPATIAL_LIVE_FRAME_SILENCE_MS = 4_000;
export const SPATIAL_ANNOTATED_FRAME_SILENCE_MS = 6_000;

export function spatialLiveFrameSilenceMs(mjpegUrl: string): number {
  const query = String(mjpegUrl || '').split('?')[1] || '';
  const kind = new URLSearchParams(query).get('view_kind');
  if (kind === 'annotated') return SPATIAL_ANNOTATED_FRAME_SILENCE_MS;
  return SPATIAL_LIVE_FRAME_SILENCE_MS;
}

function logicalMjpegUrl(url: string): string {
  return String(url || '')
    .replace(/([?&])_=\d+/g, '')
    .replace(/([?&])retry=\d+/g, '')
    .replace(/[?&]$/, '');
}

function sameJpeg(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length || left.length === 0) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

const OVERLAY_FILL = 'rgba(29, 78, 216, 0.16)';
const OVERLAY_STROKE = 'rgba(29, 78, 216, 0.94)';

export type SpatialOperationsLiveCameraProps = {
  status: SpatialOperationsLiveCameraState;
  mjpegUrl?: string | null;
  snapshotUrl?: string | null;
  overlays?: OverviewAreaGeometry[] | null;
  focusZoneId?: string | null;
  cameraLabel?: string | null;
  connectingLabel?: string | null;
  onError?: React.ReactEventHandler<HTMLImageElement>;
  /** Distinct JPEG painted. Not a repeated or held frame. */
  onFreshFrame?: () => void;
  /** The painted JPEG is no longer a fresh frame. The picture may stay up. */
  onFrameStale?: () => void;
  /** Test double. Production reads the multipart body once and notices a clean end. */
  streamRelay?: (args: MjpegRelayArgs) => Promise<MjpegRelayResult>;
  /**
   * Keep the last painted JPEG across a stall or a new stream URL.
   * Showcase Original sets this so the dark booth placeholder cannot replace a frame.
   */
  retainPaintedFrame?: boolean;
};

function objectUrlForJpeg(jpeg: Uint8Array): string {
  const blob = new Blob([Uint8Array.from(jpeg)], { type: 'image/jpeg' });
  if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
    return URL.createObjectURL(blob);
  }
  let binary = '';
  jpeg.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return `data:image/jpeg;base64,${btoa(binary)}`;
}

function revokeFrameUrl(url: string | null): void {
  if (url && url.startsWith('blob:') && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(url);
  }
}

function provenOverlays(overlays?: OverviewAreaGeometry[] | null): OverviewAreaGeometry[] {
  return (Array.isArray(overlays) ? overlays : []).filter(
    (item) => item?.polygon && item.polygon.length >= 3 && item.deployed !== false,
  );
}

function ZoneOverlay({
  overlays,
  focusZoneId,
}: {
  overlays: OverviewAreaGeometry[];
  focusZoneId?: string | null;
}) {
  const drawn = overlays.filter((item) => item.polygon && item.polygon.length >= 3);
  if (!drawn.length) return null;
  return (
    <svg
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      data-testid="spatial-operations-zone-overlay"
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    >
      {drawn.map((geometry, index) => {
        const focused = Boolean(focusZoneId && geometry.zoneId && geometry.zoneId === focusZoneId);
        const points = (geometry.polygon || []).map(([x, y]) => `${x},${y}`).join(' ');
        return (
          <polygon
            key={`${geometry.zoneId || geometry.label || 'zone'}-${index}`}
            data-testid="spatial-operations-zone-overlay-polygon"
            data-zone-id={geometry.zoneId || ''}
            data-focused={focused ? 'true' : 'false'}
            points={points}
            fill={OVERLAY_FILL}
            stroke={focused ? 'rgba(15, 23, 42, 0.96)' : OVERLAY_STROKE}
            strokeWidth={focused ? 0.012 : 0.008}
          />
        );
      })}
    </svg>
  );
}

function statusCopy(
  status: SpatialOperationsLiveCameraState,
  connectingLabel?: string | null,
): string | null {
  if (status === 'live') return SPATIAL_LIVE_CAMERA_LABEL;
  if (status === 'connecting') return connectingLabel || SPATIAL_LIVE_CONNECTING_LABEL;
  if (status === 'fallback_snapshot' || status === 'error') return SPATIAL_LIVE_FALLBACK_LABEL;
  if (status === 'offline') return SPATIAL_CAMERA_OFFLINE_LABEL;
  return null;
}

export function SpatialOperationsLiveCamera({
  status,
  mjpegUrl,
  snapshotUrl,
  overlays,
  focusZoneId,
  cameraLabel,
  connectingLabel,
  onError,
  onFreshFrame,
  onFrameStale,
  streamRelay,
  retainPaintedFrame = false,
}: SpatialOperationsLiveCameraProps) {
  const zones = provenOverlays(overlays);
  // A selected URL opens /mjpeg even when status is idle, error, or 404.
  // That GET is what creates viewer demand and starts ffmpeg.
  const requestedSrc = status === 'offline' ? '' : String(mjpegUrl || '').trim();
  const logicalSrc = logicalMjpegUrl(requestedSrc);
  const retryAttemptRef = useRef(0);
  const logicalKeyRef = useRef(logicalSrc);
  const [retryGeneration, setRetryGeneration] = useState(0);
  if (logicalKeyRef.current !== logicalSrc) {
    logicalKeyRef.current = logicalSrc;
    retryAttemptRef.current = 0;
    if (retryGeneration !== 0) setRetryGeneration(0);
  }
  const liveSrc = requestedSrc && retryGeneration > 0
    ? `${requestedSrc}${requestedSrc.includes('?') ? '&' : '?'}retry=${retryGeneration}`
    : requestedSrc;
  const posterSrc = String(snapshotUrl || '').trim();
  const [frameUrl, setFrameUrl] = useState<string | null>(null);
  const [heldFrameStale, setHeldFrameStale] = useState(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onFreshFrameRef = useRef(onFreshFrame);
  onFreshFrameRef.current = onFreshFrame;
  const onFrameStaleRef = useRef(onFrameStale);
  onFrameStaleRef.current = onFrameStale;
  const relayRef = useRef(streamRelay);
  relayRef.current = streamRelay;
  const lastJpegRef = useRef<Uint8Array | null>(null);
  const frameUrlRef = useRef<string | null>(null);
  const interruptedRef = useRef(false);

  const noteStreamInterrupted = useCallback(() => {
    setHeldFrameStale(true);
    onFrameStaleRef.current?.();
    if (interruptedRef.current) return;
    interruptedRef.current = true;
    const notify = onErrorRef.current;
    if (!notify) return;
    notify({} as React.SyntheticEvent<HTMLImageElement>);
  }, []);

  useEffect(() => {
    if (!liveSrc) {
      lastJpegRef.current = null;
      frameUrlRef.current = null;
      setFrameUrl((prev) => {
        revokeFrameUrl(prev);
        return null;
      });
      return undefined;
    }
    const controller = new AbortController();
    let stopped = false;
    let retryTimer: number | undefined;
    interruptedRef.current = false;
    lastJpegRef.current = null;
    const relay = relayRef.current ?? defaultMjpegStreamRelay;
    void relay({
      url: liveSrc,
      signal: controller.signal,
      onFrame: (jpeg) => {
        if (stopped) return;
        const previous = lastJpegRef.current;
        if (previous && sameJpeg(previous, jpeg)) return;
        lastJpegRef.current = jpeg.slice();
        retryAttemptRef.current = 0;
        interruptedRef.current = false;
        setHeldFrameStale(false);
        onFreshFrameRef.current?.();
        const next = objectUrlForJpeg(jpeg);
        frameUrlRef.current = next;
        setFrameUrl((prev) => {
          if (prev && prev !== next && prev.startsWith('blob:') && typeof URL.revokeObjectURL === 'function') {
            URL.revokeObjectURL(prev);
          }
          return next;
        });
      },
    }).then((result) => {
      if (stopped || result === 'aborted') return;
      noteStreamInterrupted();
      const nextAttempt = retryAttemptRef.current + 1;
      retryTimer = window.setTimeout(() => {
        if (stopped) return;
        retryAttemptRef.current = nextAttempt;
        setRetryGeneration((value) => value + 1);
      }, selectedViewMjpegRetryDelayMs(nextAttempt));
    });
    return () => {
      stopped = true;
      if (retryTimer != null) window.clearTimeout(retryTimer);
      controller.abort();
      // Leave the painted JPEG in place. Dropping it here is the dark empty
      // stage while the next GET is still opening.
    };
  }, [liveSrc, noteStreamInterrupted]);

  useEffect(() => () => {
    revokeFrameUrl(frameUrlRef.current);
    frameUrlRef.current = null;
  }, []);

  useEffect(() => {
    if (!liveSrc || status !== 'live' || heldFrameStale || !frameUrl) return undefined;
    const silenceMs = spatialLiveFrameSilenceMs(liveSrc);
    const timer = window.setTimeout(() => {
      noteStreamInterrupted();
    }, silenceMs);
    return () => window.clearTimeout(timer);
  }, [liveSrc, status, frameUrl, heldFrameStale, noteStreamInterrupted]);

  // A dead annotated relay still replaces its JPEG with the poster. Original
  // keeps the painted frame so a stall does not flash the dark placeholder.
  const mediaSrc = heldFrameStale && !retainPaintedFrame
    ? posterSrc
    : (frameUrl || posterSrc);
  const hasFreshFrame = Boolean(frameUrl) && !heldFrameStale;
  const presentedStatus: SpatialOperationsLiveCameraState = status === 'live' && heldFrameStale
    ? 'connecting'
    : status === 'connecting' && hasFreshFrame
      ? 'live'
      : status;
  const label = statusCopy(presentedStatus, connectingLabel);
  const alt = cameraLabel ? `${cameraLabel} camera` : 'Camera';

  return (
    <Box
      data-testid="spatial-operations-live-camera"
      data-live-status={status}
      data-retain-painted-frame={retainPaintedFrame ? 'true' : 'false'}
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: { xs: 200, md: 380 },
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: '#0f172a',
        border: '1px solid #e3e8ef',
      }}
    >
      {status === 'offline' ? (
        <Box
          data-testid="spatial-operations-camera-offline"
          sx={{
            minHeight: { xs: 200, md: 380 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            bgcolor: '#e9eef6',
          }}
        >
          <Typography color="text.secondary">{SPATIAL_CAMERA_OFFLINE_LABEL}</Typography>
        </Box>
      ) : null}

      {status !== 'offline' && (liveSrc || mediaSrc) ? (
        <Box
          data-testid={liveSrc ? 'spatial-operations-live-stream' : 'spatial-operations-live-poster'}
          data-media-kind={heldFrameStale ? 'stale' : liveSrc ? 'live' : 'snapshot'}
          sx={{ position: 'relative', width: '100%', minHeight: { xs: 200, md: 380 }, height: { xs: 220, md: 400 } }}
        >
          {heldFrameStale && !mediaSrc ? null : (
            <ContainedMediaOverlay
              src={mediaSrc}
              alt={alt}
              overlay={zones.length ? <ZoneOverlay overlays={zones} focusZoneId={focusZoneId} /> : null}
              imageStyle={{ zIndex: 1 }}
              onError={!heldFrameStale && frameUrl ? onError : undefined}
            />
          )}
        </Box>
      ) : null}

      {status !== 'offline' && !liveSrc && !mediaSrc ? (
        <Box
          data-testid="spatial-operations-no-snapshot"
          sx={{
            minHeight: { xs: 200, md: 380 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            bgcolor: '#e9eef6',
          }}
        >
          <Typography color="text.secondary">Current camera view is unavailable.</Typography>
        </Box>
      ) : null}

      {label && status !== 'offline' ? (
        <Typography
          variant="caption"
          data-testid={
            presentedStatus === 'fallback_snapshot'
              ? 'spatial-operations-live-fallback'
              : presentedStatus === 'connecting'
                ? 'spatial-operations-connecting'
                : 'spatial-operations-live-status'
          }
          data-connecting={presentedStatus === 'connecting' ? 'true' : 'false'}
          data-held-frame-stale={heldFrameStale ? 'true' : 'false'}
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            zIndex: 3,
            bgcolor: presentedStatus === 'live' ? 'rgba(18, 116, 74, 0.92)' : 'rgba(15, 23, 42, 0.78)',
            color: '#fff',
            fontWeight: 800,
            letterSpacing: 0.4,
            px: 1,
            py: 0.4,
            borderRadius: 0.75,
            maxWidth: 'calc(100% - 24px)',
          }}
        >
          {label}
        </Typography>
      ) : null}
    </Box>
  );
}

export default SpatialOperationsLiveCamera;
